import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Loader2 } from 'lucide-react';
import { placeOrder } from '../../services/brokerageService';
import type { PlaceOrderRequest, BrokerageError } from '../../services/brokerageService';

interface OrderTicketProps {
  symbol: string;
  currentPrice: number;
  isOpen: boolean;
  onClose: () => void;
  defaultSide?: 'buy' | 'sell';
}

type Side = 'buy' | 'sell';
type OrderType = 'market' | 'limit';

function getErrorMessage(error: unknown): string {
  const brokerageErr = error as BrokerageError;
  const code = brokerageErr?.response?.data?.error?.code;

  switch (code) {
    case 'insufficient_funds':
      return 'Insufficient funds in your account';
    case 'market_closed':
      return 'Market is currently closed for this asset';
    case 'account_not_linked':
      return 'Please link your brokerage account first';
    case 'brokerage_unavailable':
      return 'Brokerage service is temporarily unavailable';
    default:
      return brokerageErr?.message || 'Failed to place order. Please try again.';
  }
}

const OrderTicket: React.FC<OrderTicketProps> = ({ symbol, currentPrice, isOpen, onClose, defaultSide }) => {
  const [side, setSide] = useState<Side>(defaultSide ?? 'buy');
  const [orderType, setOrderType] = useState<OrderType>('market');
  const [quantity, setQuantity] = useState<number>(1);
  const [limitPrice, setLimitPrice] = useState<number>(currentPrice);
  const [successRef, setSuccessRef] = useState<string | null>(null);

  // Reset form when opened with a new symbol
  useEffect(() => {
    if (isOpen) {
      setSide(defaultSide ?? 'buy');
      setOrderType('market');
      setQuantity(1);
      setLimitPrice(currentPrice);
      setSuccessRef(null);
    }
  }, [isOpen, symbol, currentPrice, defaultSide]);

  // Auto-close after success
  useEffect(() => {
    if (successRef) {
      const timer = setTimeout(() => {
        onClose();
        setSuccessRef(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successRef, onClose]);

  const mutation = useMutation({
    mutationFn: ({ order, key }: { order: PlaceOrderRequest; key: string }) =>
      placeOrder(order, key),
    onSuccess: (data) => {
      setSuccessRef(data.brokerage_order_ref || data.order_id || 'N/A');
    },
  });

  if (!isOpen) return null;

  const effectivePrice = orderType === 'limit' ? limitPrice : currentPrice;
  const total = quantity * effectivePrice;

  const handleSubmit = () => {
    // Generate idempotency key at submit time, never stored in state
    const idempotencyKey = crypto.randomUUID();
    const order: PlaceOrderRequest = {
      symbol,
      side,
      order_type: orderType,
      quantity,
      limit_price: orderType === 'limit' ? limitPrice : null,
    };
    mutation.mutate({ order, key: idempotencyKey });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-sm shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/60">
          <h3 className="text-base font-semibold text-white">
            Order — <span className="text-teal-400">{symbol}</span>
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Success state */}
          {successRef && (
            <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-lg px-4 py-3 text-sm text-emerald-300">
              Order placed — Ref: {successRef}
            </div>
          )}

          {/* Error state */}
          {mutation.isError && (
            <div className="bg-red-500/15 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-300">
              {getErrorMessage(mutation.error)}
            </div>
          )}

          {!successRef && (
            <>
              {/* Side toggle */}
              <div className="flex rounded-lg overflow-hidden border border-slate-700">
                <button
                  onClick={() => setSide('buy')}
                  className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                    side === 'buy'
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  Buy
                </button>
                <button
                  onClick={() => setSide('sell')}
                  className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                    side === 'sell'
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  Sell
                </button>
              </div>

              {/* Order type toggle */}
              <div className="flex rounded-lg overflow-hidden border border-slate-700">
                <button
                  onClick={() => setOrderType('market')}
                  className={`flex-1 py-2 text-sm transition-colors ${
                    orderType === 'market'
                      ? 'bg-slate-600 text-white font-medium'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  Market
                </button>
                <button
                  onClick={() => setOrderType('limit')}
                  className={`flex-1 py-2 text-sm transition-colors ${
                    orderType === 'limit'
                      ? 'bg-slate-600 text-white font-medium'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  Limit
                </button>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">Quantity</label>
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Limit price (conditional) */}
              {orderType === 'limit' && (
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Limit Price</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(parseFloat(e.target.value) || 0)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              )}

              {/* Live total */}
              <div className="flex justify-between items-center py-2 border-t border-slate-700/60">
                <span className="text-sm text-slate-400">Estimated Total</span>
                <span className="text-base font-semibold text-slate-100">
                  {new Intl.NumberFormat('en-ZA', {
                    style: 'currency',
                    currency: 'ZAR',
                    minimumFractionDigits: 2,
                  }).format(total)}
                </span>
              </div>

              {/* Current price hint */}
              <p className="text-xs text-slate-500">
                Market price: {currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={mutation.isPending || quantity < 1}
                className={`w-full py-3 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                  side === 'buy'
                    ? 'bg-green-600 hover:bg-green-500 text-white'
                    : 'bg-red-600 hover:bg-red-500 text-white'
                }`}
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Placing order…
                  </>
                ) : (
                  `${side === 'buy' ? 'Buy' : 'Sell'} ${quantity} × ${symbol}`
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderTicket;
